import React, { useEffect } from 'react';
import { useDocumentTitle } from '../../hooks/use-document-title';
import {
  ContentAndButtonsContainer,
  NavbarAndContentContainer,
} from '../../layout/layout-container.styled';
import { DefaultComponentProps } from '../../app-router/component-props';
import { useAuth } from '../../../context/auth-provider';
import { can, PERMISSIONS } from '../../../auth/permissions';
import { VisuallyHidden } from '../../../style-utils/text';
import { StateSwitch } from '../../../motion';
import { ListSkeleton } from '../dashboard/dashboard-shared-components/load-state';
import { DashboardBuildingRoster } from '../dashboard/dashboard-units-components/dashboard-building-roster';
import {
  useBuildings,
  useRememberedBuilding,
} from '../dashboard/dashboard-units-components/units-shared';
import { BackBar, BackButton } from '../../layout/back-button';
import { BuildingOption } from '../dashboard/dashboard-units-components/units-shared';
import {
  Banner,
  Label,
  Select,
} from '../dashboard/dashboard-units-components/dashboard-units.styled';
import {
  BannerWrapper,
  LoadingWrapper,
  MessageCard,
  MessageText,
  MessageTitle,
  PageBody,
  PickerBar,
  PickerField,
} from './roster-page.styled';

/**
 * "Who's Home" — the resident roster as a STANDALONE page.
 *
 * The roster already existed, but only inside /dashboard, which admits owners
 * and property managers alone. A concierge can hold occupancy:read and still
 * had no route to it, so the same component is hosted here instead, reachable
 * from the main menu.
 *
 * This page owns the building choice (a concierge may cover more than one) and
 * hands it to the roster as a controlled value; the roster's own picker stands
 * down so the screen never shows two.
 */

export const resolveRosterBuildingId = (
  buildings: BuildingOption[],
  selectedContextId: number | null,
  rememberedId: number | null
): number | null => {
  if (
    selectedContextId !== null &&
    buildings.some(building => building.id === selectedContextId)
  ) {
    return selectedContextId;
  }

  return buildings.find(building => building.id === rememberedId)?.id ?? null;
};

// The rail, the building panel and <main> come from DeskShell now
// (layout/desk-shell.tsx); this screen renders only what goes inside the card.
export const RosterPage: React.FC<DefaultComponentProps> = ({
  selectedBuilding,
  onSelectBuilding,
}) => {
  useDocumentTitle("Who's home");

  const { permissions } = useAuth();
  // The server enforces this too — the check here only decides whether to show
  // an explanation instead of firing a request that is going to 403.
  const allowed = can(permissions, PERMISSIONS.OCCUPANCY_READ);

  const {
    buildings,
    loading: buildingsLoading,
    error: buildingsError,
  } = useBuildings(allowed);
  // Same remembered selection the Units and dashboard Roster screens use, so
  // moving between them never re-asks which building you are working today.
  const [rememberedId, setRememberedId] = useRememberedBuilding();
  const selectedContextId = selectedBuilding
    ? Number(selectedBuilding.id)
    : null;
  const accessibleContextId = resolveRosterBuildingId(
    buildings,
    selectedContextId,
    null
  );

  // Never trust the remembered id on its own: it survives a logout, so it can
  // name a building this user cannot see. Only an id present in the fetched
  // list is allowed to reach the roster.
  const buildingId = resolveRosterBuildingId(
    buildings,
    selectedContextId,
    rememberedId
  );

  // With exactly one building there is nothing to choose — pick it and hide the
  // picker entirely. With several, land on the first rather than an empty page.
  useEffect(() => {
    if (!allowed || buildingsLoading || buildings.length === 0) {
      return;
    }

    if (accessibleContextId !== null && rememberedId !== accessibleContextId) {
      setRememberedId(accessibleContextId);
    } else if (buildingId === null) {
      setRememberedId(buildings[0].id);
    }
  }, [
    accessibleContextId,
    allowed,
    buildings,
    buildingsLoading,
    buildingId,
    rememberedId,
    setRememberedId,
  ]);

  const selectRosterBuilding = (nextBuildingId: number | null) => {
    setRememberedId(nextBuildingId);
    if (nextBuildingId !== null) {
      onSelectBuilding?.(String(nextBuildingId));
    }
  };

  const showPicker = buildings.length > 1;

  // ONE WORD FOR WHAT THE BODY IS, so the fills crossfade inside the same box
  // (StateSwitch below) instead of a spinner snapping into a message or a
  // list. The floor keeps the card the same height across them.
  const bodyState = !allowed
    ? 'denied'
    : buildingsLoading
      ? 'loading'
      : buildingsError
        ? 'error'
        : buildings.length === 0
          ? 'empty'
          : 'ready';

  const renderBody = () => {
    if (!allowed) {
      return (
        <MessageCard>
          <MessageTitle>You don&apos;t have access to the roster</MessageTitle>
          <MessageText>
            Seeing who lives in a building needs the resident-roster permission.
            Ask an owner or property manager on your team to grant it, then open
            this page again.
          </MessageText>
        </MessageCard>
      );
    }

    if (buildingsLoading) {
      // The shape of the roster that is coming, not a spinner: the rows land
      // where these are. (The roster component brings its own indicator for
      // its second read; that one is its own.)
      return (
        <LoadingWrapper>
          <ListSkeleton rows={4} label="the roster" />
        </LoadingWrapper>
      );
    }

    if (buildingsError) {
      return (
        <BannerWrapper>
          <Banner tone="alert">{buildingsError}</Banner>
        </BannerWrapper>
      );
    }

    if (buildings.length === 0) {
      return (
        <MessageCard>
          <MessageTitle>No buildings yet</MessageTitle>
          <MessageText>
            You aren&apos;t assigned to a building, so there is no roster to
            show. An owner or property manager can add you to one.
          </MessageText>
        </MessageCard>
      );
    }

    return (
      <>
        {showPicker && (
          <PickerBar>
            <PickerField>
              <Label htmlFor="roster-page-building">Building</Label>
              <Select
                id="roster-page-building"
                value={buildingId ?? ''}
                onChange={event =>
                  selectRosterBuilding(
                    event.target.value ? Number(event.target.value) : null
                  )
                }
              >
                {buildings.map(building => (
                  <option key={building.id} value={building.id}>
                    {building.name}
                  </option>
                ))}
              </Select>
            </PickerField>
          </PickerBar>
        )}
        {/* showManageUnits is off: it links into /dashboard, which a concierge
            cannot open — the guard would bounce them straight back out.
            hostShowsBuildingPicker is the same `showPicker` that decides the
            PickerBar above, so an empty roster can only say "switch it above"
            when there is genuinely a picker above it. */}
        <DashboardBuildingRoster
          buildingId={buildingId}
          showManageUnits={false}
          hostShowsBuildingPicker={showPicker}
        />
      </>
    );
  };

  return (
    <NavbarAndContentContainer>
      <ContentAndButtonsContainer dashboard>
        {/* TOP LEFT, one design, one component — see
            components/layout/back-button.tsx. It replaced a bottom-left
            <NavButton direction="back">, which put the way OFF this screen
            in the same corner and the same shape as the wizard's
            step-back. */}
        {/* $gutter="lg" matches PickerBar's own `spacing.lg` gutter — this
            container is the dashboard variant and pads nothing, so without
            it the button sat in the corner of the card while everything
            below it started 32px in. */}
        <BackBar $gutter="lg">
          <BackButton to="/main-menu" label="Back to menu" />
        </BackBar>
        {/* THE PAGE'S ONE H1, and it was missing entirely — measured:
            zero heading elements of any level on this route. A top-level
            view whose title is not a heading gives a screen-reader user
            nothing to jump to and starts the document outline at level 2.

            VISUALLY HIDDEN rather than drawn: the title this page needs is
            the one `useDocumentTitle` already sets, the screen's visual
            design puts its identity in the back bar and the content, and
            adding a drawn heading here would be a design change smuggled
            into an accessibility fix. */}
        <VisuallyHidden as="h1">{"Who's home"}</VisuallyHidden>
        <PageBody>
          <StateSwitch state={bodyState} minHeight={320}>
            {renderBody()}
          </StateSwitch>
        </PageBody>
      </ContentAndButtonsContainer>
    </NavbarAndContentContainer>
  );
};
